import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { TopBar } from '@/components/TopBar';
import { AppFormDialog } from '@/components/AppFormDialog';
import { HealthDot } from '@/components/HealthDot';
import { AppIcon } from '@/components/AppIcon';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { timeAgo } from '@/lib/time';
import {
  Plus, Pencil, Trash2, ChevronUp, ChevronDown, ExternalLink, Settings2, PackageOpen, Clock,
} from 'lucide-react';

export default function Admin() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['apps', 'all'],
    queryFn: async () => (await api.get('/apps')).data,
  });

  const { data: health } = useQuery({
    queryKey: ['apps', 'health'],
    queryFn: async () => (await api.get('/apps/health')).data,
    refetchInterval: 30000,
  });

  const healthMap = React.useMemo(() => {
    const m = {};
    (health?.results || []).forEach((r) => { m[r.id] = r; });
    return m;
  }, [health]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['apps'] });
  };

  const createMut = useMutation({
    mutationFn: (payload) => api.post('/apps', payload),
    onSuccess: () => { invalidate(); setDialogOpen(false); toast.success('App added'); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/apps/${id}`, payload),
    onSuccess: () => { invalidate(); setDialogOpen(false); toast.success('App updated'); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/apps/${id}`),
    onSuccess: () => { invalidate(); setDeleteTarget(null); toast.success('App deleted'); },
    onError: () => toast.error('Could not delete app'),
  });
  const reorderMut = useMutation({
    mutationFn: (ordered_ids) => api.post('/apps/reorder', { ordered_ids }),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Could not reorder'),
  });

  const handleSave = async (payload) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
    } else {
      await createMut.mutateAsync(payload);
    }
  };

  const toggleEnabled = (app, value) => {
    updateMut.mutate({ id: app.id, payload: { enabled: value } });
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= apps.length) return;
    const ids = apps.map((a) => a.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMut.mutate(ids);
  };

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (app) => { setEditing(app); setDialogOpen(true); };

  return (
    <div className="cx-page pb-16">
      <TopBar />
      <main className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8">
        <section className="pt-10 pb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <p className="font-mono text-xs text-[color:var(--cx-primary)] tracking-wide">ADMIN</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-[color:var(--cx-fg)]">Manage apps</h1>
            <p className="mt-2 text-[color:var(--cx-fg-muted)]">Add, edit, reorder and toggle the apps that appear in your portal.</p>
          </motion.div>
          <Button onClick={openAdd} data-testid="admin-add-app-button"><Plus className="h-4 w-4 mr-1.5" />Add app</Button>
        </section>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl bg-[color:var(--cx-surface-solid)]" />)}
          </div>
        ) : apps.length === 0 ? (
          <div className="cx-glass rounded-2xl p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center">
              <PackageOpen className="h-6 w-6 text-[color:var(--cx-muted)]" />
            </div>
            <h3 className="mt-4 font-display text-lg text-[color:var(--cx-fg)]">No apps yet</h3>
            <p className="mt-1 text-sm text-[color:var(--cx-fg-muted)]">Add your first app to get started.</p>
            <Button onClick={openAdd} className="mt-5"><Plus className="h-4 w-4 mr-1.5" />Add an app</Button>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="admin-app-list">
            {apps.map((app, index) => {
              return (
                <motion.div key={app.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                  className="cx-glass rounded-2xl p-3 sm:p-4 flex items-center gap-3" data-testid="admin-app-row" data-slug={app.slug}>
                  {/* Reorder */}
                  <div className="flex flex-col">
                    <button onClick={() => move(index, -1)} disabled={index === 0} data-testid="admin-app-move-up"
                      className="h-6 w-6 rounded flex items-center justify-center text-[color:var(--cx-muted)] hover:text-[color:var(--cx-fg)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => move(index, 1)} disabled={index === apps.length - 1} data-testid="admin-app-move-down"
                      className="h-6 w-6 rounded flex items-center justify-center text-[color:var(--cx-muted)] hover:text-[color:var(--cx-fg)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>

                  <AppIcon app={app} size={40} iconClass="h-5 w-5" className="shrink-0" />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-[color:var(--cx-fg)]">{app.name}</span>
                      <span className="font-mono text-xs text-[color:var(--cx-muted)]">/{app.slug}</span>
                      {app.open_in_new_tab && <Badge variant="outline" className="text-[10px]"><ExternalLink className="h-3 w-3 mr-1" />new tab</Badge>}
                      {!app.enabled && <Badge variant="outline" className="text-[10px] text-[color:var(--cx-muted)]">hidden</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <HealthDot status={healthMap[app.id]?.status || 'checking'} latency={healthMap[app.id]?.latency_ms} showLabel />
                      <span className="text-[color:var(--cx-muted)]">·</span>
                      <span className="truncate font-mono text-xs text-[color:var(--cx-muted)]">{app.url || 'no url set'}</span>
                    </div>
                    <div className="mt-0.5 text-xs" data-testid="admin-app-last-opened" data-slug={app.slug}>
                      {app.last_opened ? (
                        <span className="inline-flex items-center gap-1 text-[color:var(--cx-fg-muted)]">
                          <Clock className="h-3 w-3 text-[color:var(--cx-primary)]" />
                          Last opened by {app.last_opened.user_name} · {timeAgo(app.last_opened.at)}
                        </span>
                      ) : (
                        <span className="text-[color:var(--cx-muted)] opacity-70">Not opened yet</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <div className="hidden sm:flex items-center gap-2 mr-1">
                      <Switch checked={app.enabled} onCheckedChange={(v) => toggleEnabled(app, v)} data-testid="admin-app-enabled-switch" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(app)} data-testid="admin-edit-app-button" title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(app)} data-testid="admin-delete-app-button" title="Delete"
                      className="text-[color:var(--destructive)] hover:text-[color:var(--destructive)]">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <AppFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
        saving={createMut.isPending || updateMut.isPending}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[color:var(--cx-surface-solid)] border-[color:var(--cx-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the app and its shortcut <span className="font-mono">/{deleteTarget?.slug}</span> from your portal. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMut.mutate(deleteTarget.id)}
              className="bg-[color:var(--destructive)] text-white hover:opacity-90" data-testid="admin-confirm-delete-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
